import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { ENTITIES } from "../../services/contractService";
import { getExpiringContracts, getExpirySettings, extendContractExpiry } from "../../services/expiryService";

const DEPARTMENTS = ["HR/Admin","Finance","Operations & Asset","IT","Corp. Comms","SCM","Legal","HSE","Commercial","Engineering","Executive"];

const getUrgencyColor = (daysRemaining) => {
  if (daysRemaining <= 0) return "text-red-700";
  if (daysRemaining <= 7) return "text-orange-600";
  if (daysRemaining <= 30) return "text-yellow-600";
  return "text-green-700";
};

const getUrgencyBgColor = (daysRemaining) => {
  if (daysRemaining <= 0) return "bg-red-700/30";
  if (daysRemaining <= 7) return "bg-orange-600/30";
  if (daysRemaining <= 30) return "bg-yellow-600/30";
  return "bg-green-700/30";
};

const StatCard = ({ label, value, icon, color, subtext }) => (
  <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider">
          {label}
        </div>
        <div style={{ color }} className="text-4xl font-serif font-semibold leading-none">
          {value}
        </div>
        {subtext && <div className="text-xs text-slate-400 mt-1.5">{subtext}</div>}
      </div>
      <div className="text-2xl opacity-60">{icon}</div>
    </div>
  </div>
);

export const ExpiryManagement = () => {
  const navigate = useNavigate();
  const { service, addNotification } = useApp();
  const [contracts, setContracts] = useState([]);
  const [allContracts, setAllContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", entity: "", department: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [extendingId, setExtendingId] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const itemsPerPage = 15;

  useEffect(() => {
    loadData();
  }, [service]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Fetch ALL contracts with expiry dates (including already-expired ones)
      const allContractsData = await service.getListItems("Contracts");
      const now = new Date();

      const withExpiry = (allContractsData?.value || [])
        .filter(item => item.fields?.ExpiryDate && item.fields?.IsRenewal === "Yes")
        .map(item => {
          const contract = { ...item.fields, id: item.id };
          const expiryDate = new Date(contract.ExpiryDate);
          const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
          return {
            ...contract,
            daysRemaining,
            isExpired: daysRemaining <= 0,
            urgencyLevel: daysRemaining <= 0 ? "expired" :
                         daysRemaining <= 7 ? "critical" :
                         daysRemaining <= 30 ? "urgent" :
                         daysRemaining <= 60 ? "soon" : "upcoming",
          };
        })
        .sort((a, b) => a.daysRemaining - b.daysRemaining);

      setAllContracts(withExpiry);
    } catch (e) {
      console.error("Error loading expiry data:", e);
      addNotification("Failed to load expiry data", "error");
    } finally {
      setLoading(false);
    }
  };

  const sf = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(0); };

  // Filter contracts by status
  const getFilteredByStatus = () => {
    if (filters.status === "expired") return allContracts.filter(c => c.daysRemaining <= 0);
    if (filters.status === "critical") return allContracts.filter(c => c.daysRemaining > 0 && c.daysRemaining <= 7);
    if (filters.status === "urgent") return allContracts.filter(c => c.daysRemaining > 7 && c.daysRemaining <= 30);
    if (filters.status === "soon") return allContracts.filter(c => c.daysRemaining > 30 && c.daysRemaining <= 60);
    return allContracts;
  };

  // Apply all filters
  const filtered = getFilteredByStatus().filter(c => {
    const matchEntity = !filters.entity || c.Entity === filters.entity;
    const matchDept = !filters.department || c.Department === filters.department;
    return matchEntity && matchDept;
  });

  const paginated = filtered.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // Calculate stats
  const expired = allContracts.filter(c => c.daysRemaining <= 0);
  const critical = allContracts.filter(c => c.daysRemaining > 0 && c.daysRemaining <= 7);
  const urgent = allContracts.filter(c => c.daysRemaining > 7 && c.daysRemaining <= 30);

  const handleExtendExpiry = async (contract) => {
    if (!newExpiryDate) {
      addNotification("Please select a new expiry date", "error");
      return;
    }

    try {
      setExtendingId(contract.id);
      await extendContractExpiry(service, contract.id, newExpiryDate);
      addNotification("Contract expiry extended successfully", "success");
      setNewExpiryDate("");
      setExtendingId(null);
      loadData();
    } catch (e) {
      console.error("Error extending contract:", e);
      addNotification("Failed to extend contract", "error");
      setExtendingId(null);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-slate-100 mb-1">
          Expiry Management
        </h1>
        <p className="text-slate-400 m-0">Monitor and manage contract expirations</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent mt-2" />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Expired" value={expired.length} icon="⚠️" color="#a4262c" />
        <StatCard label="Critical (≤7d)" value={critical.length} icon="🔴" color="#ca5010" subtext="Needs immediate action" />
        <StatCard label="Urgent (8-30d)" value={urgent.length} icon="🟠" color="#c9a84c" subtext="Review soon" />
        <StatCard label="Total Monitored" value={allContracts.length} icon="📋" color="#0078d4" subtext="All expiring contracts" />
      </div>

      {/* Filters */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-5">
        <div className={`flex gap-2.5 ${showFilters ? "mb-3.5" : ""}`}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2.5 rounded-lg cursor-pointer text-sm font-sans font-semibold ${
              showFilters
                ? "bg-yellow-600/20 border border-yellow-600 text-yellow-600"
                : "bg-slate-800 border border-slate-700 text-slate-400 hover:text-slate-200"
            }`}
          >
            Filters
          </button>
          <button
            onClick={() => loadData()}
            className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 cursor-pointer text-sm font-sans hover:text-slate-200"
          >
            🔄 Refresh
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <select value={filters.status} onChange={e => sf("status", e.target.value)} className="px-2.5 py-2 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600">
              <option value="all">All Statuses</option>
              <option value="expired">Expired</option>
              <option value="critical">Critical (≤7d)</option>
              <option value="urgent">Urgent (8-30d)</option>
              <option value="soon">Soon (31-60d)</option>
            </select>
            <select value={filters.entity} onChange={e => sf("entity", e.target.value)} className="px-2.5 py-2 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600">
              <option value="">All Entities</option>
              {ENTITIES.map(o => <option key={o}>{o}</option>)}
            </select>
            <select value={filters.department} onChange={e => sf("department", e.target.value)} className="px-2.5 py-2 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600">
              <option value="">All Departments</option>
              {DEPARTMENTS.map(o => <option key={o}>{o}</option>)}
            </select>
            <button
              onClick={() => { setFilters({ status: "all", entity: "", department: "" }); setPage(0); }}
              className="px-2.5 py-2 bg-transparent border border-slate-700 rounded text-slate-400 cursor-pointer text-sm font-sans hover:bg-slate-800"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading contracts...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-950">
                  {["Ref. No.", "Title", "Entity", "Vendor", "Expiry Date", "Days Left", "Status", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium text-slate-400 uppercase tracking-wider border-b border-slate-700 whitespace-nowrap text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center p-12 text-slate-400">No contracts found.</td></tr>
                ) : paginated.map((c, i) => (
                  <tr key={i} className="border-b border-slate-700 cursor-pointer hover:bg-yellow-600/5 transition-colors">
                    <td className="px-4 py-3"><span className="font-mono text-sm text-yellow-600">{c.RefNo}</span></td>
                    <td className="px-4 py-3 font-semibold max-w-48"><div className="overflow-hidden text-ellipsis whitespace-nowrap text-slate-100">{c.Title}</div></td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{c.Entity}</td>
                    <td className="px-4 py-3 max-w-36"><div className="overflow-hidden text-ellipsis whitespace-nowrap text-slate-400">{c.VendorName}</div></td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{new Date(c.ExpiryDate).toLocaleDateString("en-GB")}</td>
                    <td className={`px-4 py-3 font-mono text-sm font-semibold ${getUrgencyColor(c.daysRemaining)}`}>
                      {c.daysRemaining <= 0 ? "EXPIRED" : c.daysRemaining + "d"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${getUrgencyColor(c.daysRemaining)} ${getUrgencyBgColor(c.daysRemaining)}`}>
                        {c.urgencyLevel.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <button onClick={() => navigate("/contracts/" + c.id)} className="px-2 py-1 bg-blue-600 border-none rounded text-white cursor-pointer text-xs font-semibold hover:bg-blue-500">
                          View
                        </button>
                        <button onClick={() => setExtendingId(c.id)} className="px-2 py-1 bg-green-700 border-none rounded text-white cursor-pointer text-xs font-semibold hover:bg-green-600">
                          Extend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700 bg-slate-950 text-sm text-slate-400">
            <div>Showing {page * itemsPerPage + 1} to {Math.min((page + 1) * itemsPerPage, filtered.length)} of {filtered.length}</div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(0)}
                disabled={page === 0}
                className={`px-3 py-1 rounded text-sm font-sans ${
                  page === 0
                    ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                    : "bg-slate-800 text-yellow-600 hover:bg-slate-700 cursor-pointer"
                }`}
              >
                First
              </button>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className={`px-3 py-1 rounded text-sm font-sans ${
                  page === 0
                    ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                    : "bg-slate-800 text-yellow-600 hover:bg-slate-700 cursor-pointer"
                }`}
              >
                Prev
              </button>
              <span className="px-3 py-1 text-yellow-600 font-semibold">Page {page + 1} of {totalPages || 1}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                className={`px-3 py-1 rounded text-sm font-sans ${
                  page === totalPages - 1
                    ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                    : "bg-slate-800 text-yellow-600 hover:bg-slate-700 cursor-pointer"
                }`}
              >
                Next
              </button>
              <button
                onClick={() => setPage(Math.max(0, totalPages - 1))}
                disabled={page === totalPages - 1}
                className={`px-3 py-1 rounded text-sm font-sans ${
                  page === totalPages - 1
                    ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                    : "bg-slate-800 text-yellow-600 hover:bg-slate-700 cursor-pointer"
                }`}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Extend Expiry Modal */}
      {extendingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-7 max-w-md w-90vw">
            <h3 className="text-slate-100 text-lg font-semibold m-0 mb-4">Extend Contract Expiry</h3>
            <p className="text-slate-400 text-sm m-0 mb-5">Select a new expiry date for this contract</p>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-slate-400 mb-2">New Expiry Date</label>
              <input
                type="date"
                value={newExpiryDate}
                onChange={e => setNewExpiryDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600"
                style={{ colorScheme: "dark" }}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setExtendingId(null); setNewExpiryDate(""); }}
                className="px-5 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-400 cursor-pointer font-semibold hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExtendExpiry(allContracts.find(c => c.id === extendingId))}
                disabled={extendingId === null}
                className="px-5 py-2.5 bg-green-700 border-none rounded text-white cursor-pointer font-semibold hover:bg-green-600"
              >
                Extend
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
